"use client"

import { useState, useEffect } from "react"
import { X, Mail, ArrowRight, Loader2, CheckCircle2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { toast } from "sonner"
import axios from "@/utils/axios"

export default function NewsletterPopup() {
  const [isOpen, setIsOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [email, setEmail] = useState("")
  const [name, setName] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)

  // Show popup if not seen/subscribed before
  useEffect(() => {
    setMounted(true)
    const hasSeen = localStorage.getItem("newsletter_popup_seen")
    const hasSubscribed = localStorage.getItem("newsletter_subscribed")

    if (!hasSeen && !hasSubscribed) {
      // Delay to not immediately bombard the user
      const timer = setTimeout(() => {
        setIsOpen(true)
      }, 4000)

      return () => clearTimeout(timer)
    }
  }, [])

  if (!mounted) return null

  const handleClose = () => {
    setIsOpen(false)
    // Remember that the user closed it so we don't annoy them for a while (e.g. 7 days)
    localStorage.setItem("newsletter_popup_seen", Date.now().toString())
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!email) {
      toast.error("Please enter a valid email address.")
      return
    }

    setIsLoading(true)

    try {
      await axios.post("/subscribe", { email, name })
      
      setIsSuccess(true)
      toast.success("Successfully subscribed to our newsletter!")
      localStorage.setItem("newsletter_subscribed", "true")
      
      // Close after a brief success message
      setTimeout(() => {
        setIsOpen(false)
      }, 2000)
    } catch (error) {
      toast.error(error.response?.data?.message || "Something went wrong. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-full max-w-xs sm:max-w-md md:max-w-lg p-0 overflow-hidden bg-card border-0 shadow-2xl rounded-2xl" aria-describedby="newsletter-description">
        <DialogTitle className="sr-only">Newsletter Subscription</DialogTitle>
        <DialogDescription id="newsletter-description" className="sr-only">
          Subscribe to our newsletter for updates and offers.
        </DialogDescription>
        <div className="relative">
          {/* Close Button */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-3 right-3 z-10 bg-white/80 hover:bg-gray-100 rounded-full shadow-sm w-8 h-8 text-gray-500"
            onClick={handleClose}
          >
            <X className="h-4 w-4" />
          </Button>

          <div className="flex flex-col sm:flex-row">
            {/* Left Section - Image/Decoration (hidden on very small screens) */}
            <div className="hidden sm:flex sm:w-2/5 bg-gradient-to-br from-primary/20 to-primary/5 items-center justify-center p-6 relative overflow-hidden">
                <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-primary via-transparent to-transparent"></div>
                <div className="relative z-10 bg-white p-4 rounded-full shadow-lg">
                    <Mail className="h-10 w-10 text-primary" strokeWidth={1.5} />
                </div>
            </div>

            {/* Right Section - Content */}
            <div className="p-6 sm:p-8 flex-1 flex flex-col justify-center space-y-4">
              {isSuccess ? (
                <div className="flex flex-col items-center justify-center text-center space-y-4 py-8">
                  <div className="bg-green-100 p-3 rounded-full">
                    <CheckCircle2 className="h-8 w-8 text-green-600" />
                  </div>
                  <h2 className="text-xl font-bold text-foreground">You're in!</h2>
                  <p className="text-sm text-muted-foreground">
                    Thank you for subscribing. Keep an eye on your inbox for our latest updates and offers.
                  </p>
                </div>
              ) : (
                <>
                  <div className="text-center sm:text-left space-y-2">
                    <h2 className="text-xl sm:text-2xl font-bold text-foreground leading-tight">
                      Join Our Newsletter
                    </h2>
                    <p className="text-sm text-muted-foreground font-medium">
                      Be the first to know about new arrivals, sales, and exclusive offers.
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4 mt-2">
                    <div className="space-y-3">
                      <input
                        type="text"
                        placeholder="First Name (Optional)"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="w-full px-4 py-2 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                      />
                      <input
                        type="email"
                        placeholder="Email Address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="w-full px-4 py-2 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isLoading}
                      className="w-full bg-primary hover:bg-primary/90 text-primary-foreground py-2 text-sm font-semibold rounded-xl shadow-md transition-all flex items-center justify-center gap-2 h-10"
                    >
                      {isLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          Subscribe <ArrowRight className="h-4 w-4" />
                        </>
                      )}
                    </Button>
                  </form>

                  <p className="text-[10px] sm:text-xs text-muted-foreground text-center sm:text-left pt-2">
                    By subscribing, you agree to our Terms of Service and Privacy Policy.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
